import { fromZonedTime, toZonedTime } from 'date-fns-tz';

/**
 * Interface for the dateUtil object structure
 */
export interface DateUtil {
  createLocalDate: (isoString?: string) => Date;
  formatDate: (date: Date, format?: string, timezone?: string) => string;
  pad2: (n: number) => string;
  extractTimeFromISO: (iso: string) => string;
  extractDateFromISO: (iso: string) => string;
  calcDaysExclWeekend: (startIso: string, endIso: string) => number;
  formatShortDate: (d: Date) => string;
  isFullDayStart: (timeStr: string) => boolean;
  isFullDayEnd: (timeStr: string) => boolean;
  getMonday: (date: Date) => Date;
  getMondayDateString: (date: Date) => string;
  createTenantTimeAsUTC: (date: string, time: string) => string | null;
  convertUTCToTenantTime: (iso: string) => Date | null;
}

/**
 * Creates a Date object that preserves the local time components from an ISO date string
 * without timezone conversion
 * @param isoString - ISO date string like "2025-03-10T08:00:00"
 * @returns Date object with the exact date and time in local timezone
 */
function createLocalDate(isoString?: string): Date {
    if (!isoString) return new Date();

    // Split the string and parse the date parts
    const [datePart, timePart] = isoString.split('T');
    const [year, month, day] = datePart.split('-').map(Number);

    // Handle time parts
    let hours = 0, minutes = 0, seconds = 0;
    if (timePart) {
        const timeParts = timePart.split(':').map(Number);
        hours = timeParts[0] || 0;
        minutes = timeParts[1] || 0;
        seconds = timeParts[2] || 0;
    }

    // Create date with local components (month is 0-indexed)
    return new Date(year, month-1, day, hours, minutes, seconds);
}

/**
 * Converts Berlin local time to UTC ISO string for API storage
 * @param date - Date in YYYY-MM-DD format
 * @param time - Time in HH:MM format
 * @param tenantTimeZone - Timezone string (defaults to 'Europe/Berlin')
 * @returns UTC ISO string or null if inputs are invalid
 */
function createTenantTimeAsUTC(date: string, time: string, tenantTimeZone: string = 'Europe/Berlin'): string | null {
    if (!date || !time) return null;
    
    const timeString = `${date}T${time}:00`;
    
    const utcDate = fromZonedTime(timeString, tenantTimeZone);
    
    return utcDate.toISOString();
}

/**
 * Converts UTC ISO string to Berlin local time for display
 * @param utcIsoString - UTC ISO string
 * @param tenantTimeZone - Timezone string (defaults to 'Europe/Berlin')
 * @returns Date object in Berlin timezone
 */
function convertUTCToTenantTime(utcIsoString: string, tenantTimeZone: string = 'Europe/Berlin'): Date | null {
    if (!utcIsoString) return null;
    
    const utcDate = new Date(utcIsoString);
    return toZonedTime(utcDate, tenantTimeZone);
}

/**
 * Formats a date to ISO string format
 * @param date - Date object to format
 * @param format - Format type (not used currently)
 * @param timezone - Timezone (not used currently)
 * @returns ISO formatted string
 */
function formatDate(date: Date, format?: string, timezone: string = 'Europe/Berlin'): string {
    const dateTimeWith2Digits = (value: number): string => value < 10 ? `0${value}` : `${value}`;
    const time = `${dateTimeWith2Digits(date.getHours())}:${dateTimeWith2Digits(date.getMinutes())}:${dateTimeWith2Digits(date.getSeconds())}`;

    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}T${time}`;
}

/**
 * Pads a number with leading zeros to make it 2 digits
 * @param n - Number to pad
 * @returns Padded string
 */
function pad2(n: number): string {
    return String(n).padStart(2, '0');
}

/**
 * Extracts time from ISO string without timezone conversion
 * @param iso - ISO datetime string
 * @returns Time in HH:MM format
 */
function extractTimeFromISO(iso: string): string {
    if (!iso) return '';
    // Accepts either 'T' or whitespace between date and time
    const timeMatch = iso.match(/[T\s](\d{2}):(\d{2})/);
    if (timeMatch) {
        return `${timeMatch[1]}:${timeMatch[2]}`;
    }
    // Fallback to Date conversion if string lacks explicit time separator
    const dt = new Date(iso);
    if (isNaN(dt.getTime())) return '';
    const hh = pad2(dt.getHours());
    const min = pad2(dt.getMinutes());
    return `${hh}:${min}`;
}

/**
 * Extracts date from ISO string without timezone conversion
 * @param iso - ISO datetime string
 * @returns Date in YYYY-MM-DD format
 */
function extractDateFromISO(iso: string): string {
    if (!iso) return '';
    const dateMatch = iso.match(/^(\d{4}-\d{2}-\d{2})/);
    if (dateMatch) {
        return dateMatch[1];
    }
    // Fallback
    const dt = new Date(iso);
    return dt.toISOString().slice(0, 10);
}

/**
 * Calculates weekdays (Monday-Friday) between two ISO date strings
 * @param startIso - Start date ISO string
 * @param endIso - End date ISO string
 * @returns Number of weekdays
 */
function calcDaysExclWeekend(startIso: string, endIso: string): number {
    if (!startIso || !endIso) return 0;
    const startDate = new Date(startIso);
    const endDate = new Date(endIso);
    if (endDate < startDate) return 0;

    let count = 0;
    let cur = new Date(startDate);
    while (cur <= endDate) {
        const dayOfWeek = cur.getDay(); // Sunday=0, Monday=1,...
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
            count++;
        }
        cur.setDate(cur.getDate() + 1);
    }
    return count;
}

/**
 * Formats a date for short display (e.g., "Tue, 11.03.")
 * @param d - Date object
 * @returns Formatted date string
 */
function formatShortDate(d: Date): string {
    if (!d || isNaN(d.getTime())) return '';
    const weekday = d.toLocaleDateString('en-GB', { weekday: 'short' }); // e.g. Tue
    const dd = pad2(d.getDate());
    const mm = pad2(d.getMonth() + 1);
    return `${weekday}, ${dd}.${mm}.`;
}

/**
 * Determines if a time represents a full day start (00:00)
 * @param timeStr - Time string in HH:MM format
 * @returns True if it's 00:00
 */
function isFullDayStart(timeStr: string): boolean {
    return timeStr === '00:00';
}

/**
 * Determines if a time represents a full day end (23:59)
 * @param timeStr - Time string in HH:MM format
 * @returns True if it's 23:59
 */
function isFullDayEnd(timeStr: string): boolean {
    return timeStr === '23:59';
}

/**
 * Gets the Monday of the week for a given date
 * @param date - Input date
 * @returns Monday of the same week
 */
function getMonday(date: Date): Date {
    const d = new Date(date);
    // Get day of week (0=Sunday, 1=Monday, ..., 6=Saturday)
    const day = d.getDay();
    
    // Calculate days to subtract to get to Monday
    const daysToSubtract = day === 0 ? 6 : day - 1; // Sunday: 6 days back, else day-1
    
    d.setDate(d.getDate() - daysToSubtract);
    d.setHours(0, 0, 0, 0); // Reset time to midnight
    return d;
}

/**
 * Gets the Monday date string (YYYY-MM-DD) for the start of the week
 * @param date - Input date
 * @returns Monday date in YYYY-MM-DD format
 */
function getMondayDateString(date: Date): string {
    const monday = getMonday(date);
    const year = monday.getFullYear();
    const month = pad2(monday.getMonth() + 1);
    const day = pad2(monday.getDate());
    return `${year}-${month}-${day}`;
}

const dateUtil: DateUtil = {
    createLocalDate: (isoString?: string) => createLocalDate(isoString),
    formatDate: (date: Date, format?: string, timezone?: string) => formatDate(date, format, timezone),
    pad2: (n: number) => pad2(n),
    extractTimeFromISO: (iso: string) => extractTimeFromISO(iso),
    extractDateFromISO: (iso: string) => extractDateFromISO(iso),
    calcDaysExclWeekend: (startIso: string, endIso: string) => calcDaysExclWeekend(startIso, endIso),
    formatShortDate: (d: Date) => formatShortDate(d),
    isFullDayStart: (timeStr: string) => isFullDayStart(timeStr),
    isFullDayEnd: (timeStr: string) => isFullDayEnd(timeStr),
    getMonday: (date: Date) => getMonday(date),
    getMondayDateString: (date: Date) => getMondayDateString(date),
    createTenantTimeAsUTC: (date: string, time: string) => createTenantTimeAsUTC(date, time),
    convertUTCToTenantTime: (iso: string) => convertUTCToTenantTime(iso)
};

export default dateUtil; 